import { Component } from '@angular/core';
import { Role } from '../../model/auth/Role';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MobileAwareComponent } from '../../common/mobile-aware/mobile-aware.component';
import { Contest } from '../../model/contest/Contest';
import { TenantService } from '../../service/tenant.service';
import { MigrationParams, MigrationType } from '../../model/migration/Migration';
import { MatSelectChange } from '@angular/material/select';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MigrationService } from '../../service/migration.service';
import { NotificationHandler } from '../../helper/notification.handler';
import { Session } from '../../model/auth/Session';
import { AuthService } from '../../service/auth.service';

@Component({
  selector: 'app-migrations-dashboard',
  templateUrl: './migrations-dashboard.component.html',
  styleUrls: ['./migrations-dashboard.component.scss']
})
export class MigrationsDashboardComponent extends MobileAwareComponent {

  readonly CONTEST_ID = 'CONTEST_ID';
  readonly CALLER_AUTH = 'CALLER_AUTH';

  readonly roles = [Role.Admin];
  readonly migrations = [
    MigrationType['count-exam-user-rating'],
    MigrationType['clear-exam-user-rating'],
    MigrationType['update-contest-end-date'],
    MigrationType['increment-school-grade'],
  ];
  readonly configs: Map<MigrationType, string> = new Map<MigrationType, string>([
    [
      MigrationType['count-exam-user-rating'],
      `
{
  "contest_id": "${this.CONTEST_ID}"
}
      `
    ],
    [
      MigrationType['clear-exam-user-rating'],
      `
{
  "contest_id": "${this.CONTEST_ID}"
}
      `
    ],
    [
      MigrationType['update-contest-end-date'],
      `
{
  "contest_id": "${this.CONTEST_ID}",
  "ends_at": "<ends_at_time_in_epoch_millis>",
  "enrol_ends_at": "<enrol_ends_at_time_in_epoch_millis___optional>",
  "skip_notification": "true"
}
      `
    ],
    [
      MigrationType['increment-school-grade'],
      `
{
  "contest_id": "${this.CONTEST_ID}",
  "authorization": "${this.CALLER_AUTH}"
}
      `
    ]
  ]);

  inProgress = false;

  competitionId?: string;
  contest?: Contest;

  migrationId?: MigrationType;

  session?: Session;

  paramsField = new FormControl('', [
    Validators.minLength(5), Validators.maxLength(2048)
  ]);

  migrationsForm: FormGroup = new FormGroup({
    params: this.paramsField,
  });

  constructor(private migrationService: MigrationService,
              private tenantService: TenantService,
              private authService: AuthService,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  initContent() {
    const tenant = this.tenantService.getTenant();
    if (tenant && tenant.id) {
      this.competitionId = tenant.id;
    }

    this.authService.getSession()
      .then(session => this.session = session)
      .catch(err => console.error(err));
  }

  migrationTypeCompareWith(m1: MigrationType, m2: MigrationType) {
    return m1 && m2 && m1 === m2;
  }

  migrationTypeSelected(change: MatSelectChange) {
    this.migrationId = change.value as MigrationType;

    if (this.configs.has(this.migrationId)) {
      let value = this.configs.get(this.migrationId) as string;

      if (value.includes(this.CALLER_AUTH) && this.session) {
        value = value.replace(this.CALLER_AUTH, this.session.token || '');
      }

      this.paramsField.setValue(value);
    }
  }

  localizeMigrationType(type: MigrationType): string {
    switch (type) {
      case MigrationType['count-exam-user-rating']:
        return $localize`:Migrations|Type count-exam-user-rating@@migrations-type-count-exam-user-rating:Count exam participants rating`;
      case MigrationType['clear-exam-user-rating']:
        return $localize`:Migrations|Type clear-exam-user-rating@@migrations-type-clear-exam-user-rating:Hide exam participants rating`;
      case MigrationType['update-contest-end-date']:
        return $localize`:Migrations|Type update-contest-end-date@@migrations-type-update-contest-end-date:Change contest end date`;
      case MigrationType['increment-school-grade']:
        return $localize`:Migrations|Type increment-school-grade@@migrations-type-increment-school-grade:Increment school grade`;
    }
  }

  get showContestSelect(): boolean {
    return !!this.migrationId
      && this.configs.has(this.migrationId)
      && (<string>this.configs.get(this.migrationId)).includes(this.CONTEST_ID);
  }

  contestSelected(contest: Contest) {
    let value = this.paramsField.value as string || '';
    value = value.replace(this.CONTEST_ID, contest.id);
    if (this.contest) {
      value = value.replace(this.contest.id, contest.id);
    }
    this.paramsField.setValue(value);
    this.contest = contest;
  }

  get canExecuteMigration(): boolean {
    return !!this.migrationId
      && (!this.configs.has(this.migrationId)
        || (this.migrationsForm.valid && Object.keys(JSON.parse(<string>this.paramsField.value)).length > 0));
  }

  executeMigration() {
    if (!this.migrationId) return;

    this.inProgress = true;

    const params = JSON.parse(<string>this.paramsField.value) as MigrationParams;

    this.migrationService.executeContestMigration(this.migrationId, params).subscribe({
      next: () => {
        this.inProgress = false;
        NotificationHandler.notification($localize`:Migrations|Migration executed successfully notification@@migrations-executed-successfully-notification:Migration executed successfully`);
      }, error: error => {
        NotificationHandler.handleError($localize`:Migrations|Migration execution failed notification@@migrations-execution-failed-notification:Migration execution failed`, error);
        this.inProgress = false;
      }
    });
  }
}
